import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';

export interface PromptDialogConfig {
  title?: string;
  message: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info' | 'success';
  required?: boolean;
  inputType?: 'text' | 'textarea';
  maxLength?: number;
}

export interface PromptDialogState {
  show: boolean;
  config: PromptDialogConfig | null;
}

@Injectable({
  providedIn: 'root'
})
export class PromptDialogService {
  // Signal for dialog state
  private dialogState = signal<PromptDialogState>({
    show: false,
    config: null
  });

  // Subject for handling prompt results
  private resultSubject = new Subject<string | null>();

  // Public readonly access to dialog state
  readonly state = this.dialogState.asReadonly();

  /**
   * Show a prompt dialog and return a promise that resolves with user's input
   */
  prompt(config: PromptDialogConfig): Promise<string | null> {
    const fullConfig: PromptDialogConfig = {
      title: 'Input Required',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      type: 'info',
      required: false,
      inputType: 'text',
      ...config
    };

    this.dialogState.set({
      show: true,
      config: fullConfig
    });

    return new Promise<string | null>((resolve) => {
      const subscription = this.resultSubject.subscribe({
        next: (result) => {
          resolve(result);
          subscription.unsubscribe();
        }
      });
    });
  }

  /**
   * Handle confirmation result with input value
   */
  handleConfirm(value: string): void {
    this.hide();
    this.resultSubject.next(value);
  }

  /**
   * Handle cancellation result
   */
  handleCancel(): void {
    this.hide();
    this.resultSubject.next(null);
  }

  /**
   * Hide the dialog
   */
  hide(): void {
    this.dialogState.set({
      show: false,
      config: null
    });
  }

  // Utility methods for common prompt types
  static cancellationReason(): PromptDialogConfig {
    return {
      title: 'Cancellation Reason',
      message: 'Please provide a reason for cancellation:',
      placeholder: 'Enter reason...',
      confirmText: 'Submit',
      cancelText: 'Cancel',
      type: 'warning',
      required: true,
      inputType: 'textarea'
    };
  }

  static reasonForChange(action: string): PromptDialogConfig {
    return {
      title: `Reason for ${action}`,
      message: `Please provide a reason for ${action}:`,
      placeholder: 'Enter reason...',
      confirmText: 'Submit',
      cancelText: 'Cancel',
      type: 'info',
      required: true,
      inputType: 'textarea'
    };
  }

  static textInput(message: string, title?: string): PromptDialogConfig {
    return {
      title: title || 'Input Required',
      message,
      placeholder: 'Enter value...',
      confirmText: 'OK',
      cancelText: 'Cancel',
      type: 'info',
      required: false,
      inputType: 'text'
    };
  }
}
